import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Check } from "lucide-react";
import { LOGO_URL } from "@/lib/content";

const API = process.env.REACT_APP_BACKEND_URL;

const initial = {
  venue_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  website: "",
  fabswingers_profile: "",
  swinghub_profile: "",
  address_line1: "",
  address_line2: "",
  city: "",
  postcode: "",
  country: "United Kingdom",
  about: "",
};

export default function VenueApply() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
      );
      // Drop empty optional fields
      for (const k of ["website", "fabswingers_profile", "swinghub_profile", "address_line2", "about"]) {
        if (!payload[k]) delete payload[k];
      }
      await axios.post(`${API}/api/venue-applications`, payload);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit — please try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-black text-[#F5F5F0] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="apply-success">
          <div className="h-16 w-16 mx-auto mb-6 rounded-full bg-[#D9A6F5]/15 border border-[#D9A6F5]/40 flex items-center justify-center">
            <Check size={28} className="text-[#D9A6F5]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-4">
            Application received.
          </h1>
          <p className="text-[#A3A3A3] leading-relaxed">
            Thanks for applying. We&apos;ll review the details and be in touch by email — usually
            within 2 working days. Once you&apos;re approved you&apos;ll get a link to set your
            password and log in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-[#F5F5F0] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <img src={LOGO_URL} alt="Swingle" className="h-8 mx-auto mb-6" />
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-2">
            Venue Sign-up
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter">
            Apply to join Swingle
          </h1>
          <p className="mt-3 text-sm text-[#A3A3A3] max-w-md mx-auto">
            Tell us a bit about your venue. If we approve you, we&apos;ll email you a link to set
            your password and access your dashboard.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <Section title="Venue">
            <Input required label="Venue name" value={form.venue_name} onChange={(v) => setField("venue_name", v)} testId="apply-venue-name" />
            <Input label="Website" type="url" value={form.website} onChange={(v) => setField("website", v)} testId="apply-website" placeholder="https://…" />
            <Textarea label="About your venue" value={form.about} onChange={(v) => setField("about", v)} testId="apply-about" placeholder="A short description — what makes your venue special?" rows={4} />
          </Section>

          <Section title="Primary contact">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input required label="Your name" value={form.contact_name} onChange={(v) => setField("contact_name", v)} testId="apply-contact-name" />
              <Input required label="Phone" type="tel" value={form.contact_phone} onChange={(v) => setField("contact_phone", v)} testId="apply-phone" />
            </div>
            <Input required label="Email" type="email" value={form.contact_email} onChange={(v) => setField("contact_email", v)} testId="apply-email" />
          </Section>

          <Section title="Venue address">
            <Input required label="Address line 1" value={form.address_line1} onChange={(v) => setField("address_line1", v)} testId="apply-line1" />
            <Input label="Address line 2 (optional)" value={form.address_line2} onChange={(v) => setField("address_line2", v)} testId="apply-line2" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input required label="City" value={form.city} onChange={(v) => setField("city", v)} testId="apply-city" />
              <Input required label="Postcode" value={form.postcode} onChange={(v) => setField("postcode", v)} testId="apply-postcode" />
              <Input label="Country" value={form.country} onChange={(v) => setField("country", v)} testId="apply-country" />
            </div>
          </Section>

          <Section title="Existing profiles (optional)">
            <Input label="FabSwingers profile URL" type="url" value={form.fabswingers_profile} onChange={(v) => setField("fabswingers_profile", v)} testId="apply-fs" placeholder="https://…" />
            <Input label="SwingHub profile URL" type="url" value={form.swinghub_profile} onChange={(v) => setField("swinghub_profile", v)} testId="apply-sh" placeholder="https://…" />
          </Section>

          <button
            type="submit"
            disabled={busy}
            data-testid="apply-submit"
            className="w-full bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-50 py-4 text-xs uppercase tracking-widest font-bold inline-flex items-center justify-center gap-2 transition-colors"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? "Sending…" : "Submit application"}
          </button>
          <p className="text-center text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]/60">
            We review each application manually — approval is not automatic.
          </p>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-white/10 p-5 md:p-6 space-y-3">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">{title}</div>
      {children}
    </div>
  );
}

function Input({ label, required, value, onChange, type = "text", testId, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
        {label} {required && <span className="text-[#D9A6F5]">*</span>}
      </span>
      <input
        required={required}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, testId, placeholder, rows = 3 }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">{label}</span>
      <textarea
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
      />
    </label>
  );
}
